'use client';

import { useEffect, useRef } from 'react';

import { track } from '@/lib/analytics';
import { onDeparture } from '@/lib/analytics/departure';
import type { Phase } from '@/lib/learning-journey/phase-fsm';
import type { JourneySnapshot } from '@/lib/learning-journey/journey';
import type { LearningJourney } from '@/lib/learning-journey/journey';

/**
 * Mesure d'usage de la progression d'apprentissage, parcours par cœur.
 *
 * Volontairement **à l'extérieur** de `LearningJourney` : la classe est une
 * couche de domaine pure (aucun import React, Studio ou DOM), et lui faire
 * connaître un client réseau casserait cette propriété et ses tests en
 * environnement node. On observe donc les instantanés depuis la frontière
 * React, comme le font déjà `useJourneyHints` et les bridges piano/métronome.
 *
 * Ce que la mesure produit :
 *
 *  - une **session de pratique** encadrée (début au montage, fin au démontage
 *    ou au départ de l'onglet), avec ses compteurs agrégés ;
 *  - les **transitions de phase** du parcours ;
 *  - les **phrases validées**, avec le nombre d'essais et le temps passé.
 *
 * Les gestes répétitifs ne sont jamais émis à l'unité : un piano-roll en
 * produirait des milliers par session. Ils sont comptés ici et résumés dans
 * `practice_session_ended`.
 *
 * Le tempo, lui, n'est plus compté ici. Il l'était par un abonnement à
 * `studio.bpm` armé exactement là où le contrôle de tempo est démonté : le
 * parcours passe par `RangeRunner` et ne touche jamais `studio.bpm`, donc
 * `tempoChanges` valait structurellement zéro. C'est `studio_session_ended`
 * qui porte désormais ce compteur, sur les écrans qui ont un curseur.
 *
 * # Deux identifiants, et un seul part dans les payloads
 *
 * `morceauId` est la clé de **progression** : c'est `sourceMidiId`, parfois
 * suffixé `:t<n>` (transposition) ou `:n<rungId>` (niveau), et il désigne un
 * fichier travaillé sous un réglage donné. `pieceId` est l'identité
 * **catalogue**, la seule qui sache rouvrir le morceau et la seule que le
 * reste du produit emploie (`piece_opened`, `studio_session_ended`). Envoyer
 * le premier sous le nom du second — ce que faisait ce fichier — rendait zéro
 * ligne à toute jointure `piece_opened` → `practice_session_ended`, donc à
 * l'entonnoir entier.
 */
export type UseJourneyAnalyticsOptions = {
  readonly journey: LearningJourney | null;
  readonly snapshot: JourneySnapshot | null;
  /** La clé de progression, qui borne la session. Voir l'en-tête. */
  readonly morceauId: string;
  /**
   * L'identité catalogue, seule à partir dans `pieceId`. `null` pour un
   * morceau qui n'est pas venu du catalogue — un import local, par exemple :
   * la mesure omet alors la propriété plutôt que d'y mettre autre chose.
   */
  readonly pieceId: string | null;
};

/**
 * L'ordre des phases, pour retenir la plus avancée.
 *
 * Une session ne monte pas toujours : `revisitUnit` rouvre le parcours d'une
 * unité **sans changer de phase**, et la tête de lecture fait revenir en
 * arrière. La dernière phase vue décrirait donc où l'on regardait en partant,
 * là où un abandon se lit par l'endroit qu'il a atteint.
 */
const PHASE_RANK: Record<Phase, number> = {
  phase1: 1,
  phase2: 2,
  repertoire: 3,
};

/**
 * Les états depuis lesquels une passe fait basculer l'unité en `DONE`.
 *
 * `PhraseFSM.handleRhythmRun` et `handleChainRun` sont les deux seuls chemins
 * qui valident, et tous deux poussent la passe dans `runHistory` puis posent
 * `DONE` dans la **même** mutation. Voir `attempts`, plus bas.
 */
const VALIDATING_STATES: ReadonlySet<string> = new Set(['S4', 'S5']);

export function useJourneyAnalytics(opts: UseJourneyAnalyticsOptions): void {
  const { journey, snapshot, morceauId, pieceId } = opts;

  const sessionStartedAtRef = useRef(0);
  /**
   * L'identité catalogue au moment d'émettre, et non celle capturée à
   * l'armement de la session.
   *
   * Une ref plutôt qu'une dépendance de l'effet de session : `pieceId` remonte
   * d'un état de `StudioPage`, donc il peut se préciser un rendu après le
   * parcours. En dépendance, il ferait alors fermer puis rouvrir la session
   * sur ce seul fait — une séance coupée en deux, et deux fois moins de temps
   * de pratique dans chaque moitié.
   */
  const pieceIdRef = useRef<string | undefined>(undefined);
  /** La phase du dernier instantané observé, pour reconnaître une transition. */
  const phaseRef = useRef<Phase | null>(null);
  /** La phase la plus avancée atteinte pendant la session. */
  const phaseReachedRef = useRef<Phase | null>(null);
  /**
   * Toutes les unités connues `DONE`, y compris celles des sessions passées.
   *
   * Amorcé au premier instantané, et c'est tout l'objet du champ : le magasin
   * restitue la progression dans le constructeur de `LearningJourney`
   * (`hydrateFromStore`, synchrone, sur la copie locale), donc le premier
   * instantané la porte **déjà**. Parti vide, il faisait relire chaque reprise
   * comme une rafale de validations neuves — sur la métrique la plus
   * importante du produit, et avec un envoi immédiat au passage puisque la
   * rafale remplit la file d'un coup.
   */
  const doneIdsRef = useRef<Set<string>>(new Set());
  /** L'amorçage est fait : le premier instantané du parcours est passé. */
  const primedRef = useRef(false);
  /** Les unités validées **pendant cette session**, seules à compter ici. */
  const validatedIdsRef = useRef<Set<string>>(new Set());
  /**
   * Les unités dont le parcours a été ouvert.
   *
   * Ouvert, et pas « jouée au moins une fois » : quitter une unité avant la
   * première passe ne laissait aucune trace, ni tentative ni abandon, alors
   * que c'est l'abandon le plus précoce et donc le plus instructif.
   */
  const attemptedIdsRef = useRef<Set<string>>(new Set());
  const attemptsByPhraseRef = useRef<Map<string, number>>(new Map());
  const phraseStartedAtRef = useRef<Map<string, number>>(new Map());
  /** Dernier état FSM connu par phrase, pour dire *où* un abandon s'est produit. */
  const lastStateByPhraseRef = useRef<Map<string, string>>(new Map());
  /** Combien d'unités compte le parcours : le dénominateur de la fin. */
  const phrasesTotalRef = useRef(0);
  /** Toutes les unités sont validées, quelle que soit la session qui l'a fait. */
  const completedRef = useRef(false);

  /** Empêche un double envoi quand le départ précède le démontage. */
  const endedRef = useRef(false);

  // Déclaré **avant** l'effet de session : React joue les effets dans l'ordre
  // de déclaration, donc l'identité est posée quand la session s'ouvre au
  // montage, et se met à jour ensuite sans toucher à la session en cours.
  useEffect(() => {
    pieceIdRef.current = pieceId ?? undefined;
  }, [pieceId]);

  // ── Cycle de vie de la session ──────────────────────────────────
  // `startSession` et `endSession` vivent *dans* l'effet : le départ de
  // l'onglet, le retour au premier plan et le démontage partagent ainsi la
  // même closure, sans référence mutable écrite pendant le rendu (React
  // interdit d'écrire dans une ref hors effet/gestionnaire).
  useEffect(() => {
    if (journey === null) {
      return;
    }

    // Ce qui décrit le **parcours** et non la séance : remis à zéro une fois
    // par morceau, jamais à la réouverture. Vider `doneIdsRef` au retour au
    // premier plan ferait relire toute la progression acquise comme une rafale
    // de validations neuves, exactement ce que l'amorçage existe pour éviter.
    doneIdsRef.current = new Set();
    primedRef.current = false;
    phrasesTotalRef.current = 0;
    completedRef.current = false;

    const startSession = () => {
      sessionStartedAtRef.current = Date.now();
      endedRef.current = false;
      validatedIdsRef.current = new Set();
      attemptedIdsRef.current = new Set();
      attemptsByPhraseRef.current = new Map();
      phraseStartedAtRef.current = new Map();
      lastStateByPhraseRef.current = new Map();

      // La première phase observée d'une session n'est pas une transition.
      // Partie de `null`, une reprise en `phase2` émettait une entrée en
      // `phase2` qui n'avait pas eu lieu : `journey_phase_entered` comptait
      // les reprises et non les franchissements — le défaut déjà corrigé pour
      // les phrases, laissé en place pour les phases. L'entrée dans le
      // parcours est dite par `practice_session_started`, et l'endroit atteint
      // par `phaseReached`, qu'on amorce ici pour la même raison : sinon une
      // session qui ne change jamais de phase n'en rapporterait aucune.
      const at = journey.getSnapshot();

      phaseRef.current = at.phase;
      phaseReachedRef.current = at.phase;

      track({
        name: 'practice_session_started',
        properties: { journey: 'by_heart', pieceId: pieceIdRef.current },
      });
    };

    const endSession = (reason: 'navigation' | 'pagehide') => {
      if (endedRef.current || sessionStartedAtRef.current === 0) {
        return;
      }

      endedRef.current = true;

      // Une phrase ouverte mais jamais validée est un abandon. C'est le
      // signal le plus utile du parcours : il désigne l'endroit précis où les
      // gens décrochent, ce que le seul comptage de validations ne dit pas.
      // Émis à la fin de session parce que « abandonné » ne peut se constater
      // qu'a posteriori — tant que la session dure, la phrase est en cours.
      for (const phraseId of attemptedIdsRef.current) {
        if (doneIdsRef.current.has(phraseId)) {
          continue;
        }

        track({
          name: 'phrase_abandoned',
          properties: {
            phraseId,
            pieceId: pieceIdRef.current,
            state: lastStateByPhraseRef.current.get(phraseId) ?? 'unknown',
            attempts: attemptsByPhraseRef.current.get(phraseId) ?? 0,
          },
        });
      }

      track({
        name: 'practice_session_ended',
        properties: {
          journey: 'by_heart',
          pieceId: pieceIdRef.current,
          durationMs: Date.now() - sessionStartedAtRef.current,
          phrasesAttempted: attemptedIdsRef.current.size,
          phrasesValidated: validatedIdsRef.current.size,
          phrasesTotal: phrasesTotalRef.current,
          phaseReached: phaseReachedRef.current ?? undefined,
          // `completed` dit l'**état du parcours** à la fermeture : plus une
          // seule unité ne reste à valider, quelle que soit la session qui l'a
          // fait. C'est la définition partagée avec le parcours harmonique
          // (`use-harmony-analytics`), et elle est écrite des deux côtés parce
          // qu'un même champ qui voudrait dire « terminé » ici et « terminé
          // aujourd'hui » là-bas ne serait comparable nulle part — or comparer
          // les deux parcours est tout l'objet de `journey`. Ce que *cette*
          // séance a produit se lit à côté, dans `phrasesValidated`.
          completed: completedRef.current,
          reason,
        },
      });
    };

    startSession();

    // `onDeparture` et non un `pagehide` nu : sur la tablette posée sur le
    // pupitre — la posture de référence du produit — l'onglet passe en
    // arrière-plan puis est tué par l'OS, et `pagehide` n'arrive jamais. Une
    // séance de vingt-cinq minutes ne laissait alors aucune trace. Il rouvre
    // aussi au retour au premier plan, et ce n'est pas facultatif : fermer
    // sans rouvrir échangerait la perte totale contre une troncature
    // systématique de toute séance interrompue par un appel téléphonique.
    const disarm = onDeparture({
      close: () => {
        endSession('pagehide');
      },
      reopen: () => {
        startSession();
      },
    });

    return () => {
      disarm();
      endSession('navigation');
      sessionStartedAtRef.current = 0;
    };
  }, [journey, morceauId]);

  // ── Transitions observées sur les instantanés ───────────────────
  useEffect(() => {
    if (snapshot === null) {
      return;
    }

    phrasesTotalRef.current = snapshot.phraseProgress.length;
    completedRef.current =
      snapshot.phraseProgress.length > 0 &&
      snapshot.phraseProgress.every((progress) => progress.etatMax === 'DONE');

    // Voir `doneIdsRef` : ce qui est déjà validé en arrivant n'est pas validé
    // maintenant. L'effet de session est déclaré au-dessus de celui-ci, donc
    // React l'a déjà rejoué pour ce parcours quand on passe ici, et
    // l'amorçage porte bien sur l'instantané du parcours courant.
    if (!primedRef.current) {
      primedRef.current = true;

      for (const progress of snapshot.phraseProgress) {
        if (progress.etatMax === 'DONE') {
          doneIdsRef.current.add(progress.id);
        }
      }
    }

    if (phaseRef.current !== snapshot.phase) {
      phaseRef.current = snapshot.phase;

      if (
        phaseReachedRef.current === null ||
        PHASE_RANK[snapshot.phase] > PHASE_RANK[phaseReachedRef.current]
      ) {
        phaseReachedRef.current = snapshot.phase;
      }

      track({
        name: 'journey_phase_entered',
        properties: {
          journey: 'by_heart',
          phase: snapshot.phase,
          phraseId: snapshot.currentPhrase?.id,
          pieceId: pieceIdRef.current,
        },
      });
    }

    const currentId = snapshot.currentPhrase?.id;

    if (currentId !== undefined) {
      if (!phraseStartedAtRef.current.has(currentId)) {
        phraseStartedAtRef.current.set(currentId, Date.now());
      }

      // Le parcours d'une unité est à l'écran : elle est travaillée, même si
      // aucune passe n'a encore été lancée. Voir `attemptedIdsRef`.
      attemptedIdsRef.current.add(currentId);

      const phraseState = snapshot.currentPhraseSnapshot?.state;

      if (phraseState !== undefined) {
        lastStateByPhraseRef.current.set(currentId, phraseState);
      }

      const attempts = snapshot.currentPhraseSnapshot?.runHistory.length ?? 0;

      if (attempts > 0) {
        attemptsByPhraseRef.current.set(currentId, attempts);
      }
    }

    for (const progress of snapshot.phraseProgress) {
      if (progress.etatMax !== 'DONE' || doneIdsRef.current.has(progress.id)) {
        continue;
      }

      doneIdsRef.current.add(progress.id);
      validatedIdsRef.current.add(progress.id);
      // Une unité validée a forcément été travaillée. Sans cette ligne, une
      // unité tenue du premier coup n'entrait dans aucune tentative et la
      // session rapportait plus de validations que d'essais.
      attemptedIdsRef.current.add(progress.id);

      const startedAt = phraseStartedAtRef.current.get(progress.id);
      const seen = attemptsByPhraseRef.current.get(progress.id) ?? 0;
      const lastState = lastStateByPhraseRef.current.get(progress.id) ?? '';

      track({
        name: 'phrase_validated',
        properties: {
          phraseId: progress.id,
          pieceId: pieceIdRef.current,
          tempoRatio: progress.tempoValideMax,
          // La passe qui valide n'est jamais visible d'ici : le FSM la pousse
          // dans `runHistory` et pose `DONE` dans la même mutation, puis
          // l'orchestrateur avance d'unité — l'instantané qui annonce la
          // validation décrit donc déjà la suivante, et le dernier compte lu
          // pour celle-ci datait de la passe d'avant. On la rajoute, et
          // seulement quand l'unité était sur un pas qui valide : une unité
          // vide passe en `DONE` depuis S0 sans avoir rien fait jouer.
          attempts: VALIDATING_STATES.has(lastState) ? seen + 1 : seen,
          durationMs: startedAt === undefined ? 0 : Date.now() - startedAt,
        },
      });
    }
  }, [snapshot]);
}
